import type * as Sentry from "@sentry/browser";
import type { BrowserClientOptions } from "@sentry/browser/types/client";
import { waitFor } from "./utils/wait-for";

declare global {
  interface Window {
    Sentry?: typeof Sentry;
  }
}

interface SentryOptions {
  dsn: BrowserClientOptions["dsn"];
  release: BrowserClientOptions["release"];
  environment: BrowserClientOptions["environment"];
  sampleRate: number;
}

export class SentryLoader {
  private static ALLOWED_URLS = [
    "fandom.com",
    "futhead.com",
    "gamespot.com",
    "giantbomb.com",
    "metacritic.com",
    "muthead.com",
    "tvguide.com",
  ];

  private static SENTRY_SCRIPT_CDN_URL =
    "//browser.sentry-cdn.com/7.55.2/bundle.min.js";
  private static SENTRY_SCRIPT_INTEGRITY =
    "sha384-grzsqzUax9BbmJWNgYTLmejARf1orUEVEvvg2sXJ60jMyjmzW/Eh8hrCFkSu6GNm";

  private tags: Record<string, string> = {};
  private readonly errorQueue: Error[] = [];
  private hub: Sentry.Hub | undefined;

  constructor(private readonly options: SentryOptions) {}

  get inserted(): boolean {
    return Array.from(document.getElementsByTagName("script")).some(
      (script: HTMLScriptElement) =>
        script.src === SentryLoader.SENTRY_SCRIPT_CDN_URL,
    );
  }

  get shouldLoad(): boolean {
    return (
      Math.random() < this.options.sampleRate &&
      SentryLoader.ALLOWED_URLS.find((url) =>
        window.location.hostname.includes(url),
      ) !== undefined
    );
  }

  get loaded(): boolean {
    return window.Sentry !== undefined;
  }

  get initialized(): boolean {
    return this.hub !== undefined;
  }

  setTags(tags: Record<string, string>): void {
    this.tags = tags;
  }

  captureException(error: Error): void {
    console.error(
      `[IE] (${this.options.release} ${this.options.environment}) Error captured:`,
      error,
    );

    if (this.options.environment === "development") {
      return;
    }

    if (!this.shouldLoad) {
      return;
    }

    if (!this.inserted) {
      this.errorQueue.push(error);
      this.loadSentry();
      return;
    }

    if (!this.loaded) {
      this.errorQueue.push(error);
      waitFor(() => this.loaded, 10, 100).then(() => {
        this.createSentryInstance();
        this.flushErrorQueue();
      });
      return;
    }

    if (!this.initialized) {
      this.createSentryInstance();
    }

    this.hub.captureException(error);
  }

  private loadSentry(): Promise<void> {
    return new Promise((resolve) => {
      const script: HTMLScriptElement = document.createElement("script");
      script.defer = true;
      script.src = SentryLoader.SENTRY_SCRIPT_CDN_URL;
      script.crossOrigin = "anonymous";
      script.integrity = SentryLoader.SENTRY_SCRIPT_INTEGRITY;
      script.onload = () => {
        this.createSentryInstance();
        this.flushErrorQueue();
        resolve();
      };

      const temp = document.getElementsByTagName("script")[0];

      temp.parentNode.append(script);
    });
  }

  private createSentryInstance(): void {
    const {
      Breadcrumbs,
      BrowserClient,
      Dedupe,
      defaultStackParser,
      HttpContext,
      Hub,
      LinkedErrors,
      makeFetchTransport,
    } = window.Sentry;
    const options: BrowserClientOptions = {
      ...this.options,
      sampleRate: 1.0,
      transport: makeFetchTransport,
      stackParser: defaultStackParser,
      integrations: [
        new Breadcrumbs(),
        new Dedupe(),
        new HttpContext(),
        new LinkedErrors(),
      ],
    };
    const client = new BrowserClient(options);
    this.hub = new Hub(client);
    this.hub.setTags({
      url: window.location.href,
      userAgent: window.navigator.userAgent,
      ...this.tags,
    });
  }

  private flushErrorQueue() {
    this.errorQueue.forEach((error) => this.hub?.captureException(error));
  }
}
